import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { 
  LoginResponse, 
  RefreshTokenResponse, 
  User, 
  ApiResponseEnvelope 
} from '@doctor-scheduling/shared/models';
import { API_BASE_URL } from './api-base-url.token';

/** HTTP client for authentication and profile endpoints. */
@Injectable({
  providedIn: 'root',
})
export class AuthApi {
  /** Angular HTTP client used for API requests. */
  private readonly http = inject(HttpClient);
  /** NestJS API origin from the web app environment. */
  private readonly apiBaseUrl = inject(API_BASE_URL);
  /** Base URL for auth routes under `/api/v1/auth`. */
  private readonly baseUrl = `${this.apiBaseUrl}/api/v1/auth`;

  /**
   * Authenticates a user with email/username and password.
   * @param credentials - Login identifier and password hash
   * @returns Observable of the login API envelope
   */
  login(credentials: { identifier: string; passwordHash: string }): Observable<ApiResponseEnvelope<LoginResponse>> {
    const body = {
      identifier: credentials.identifier,
      password: credentials.passwordHash,
    };
    return this.http.post<ApiResponseEnvelope<LoginResponse>>(`${this.baseUrl}/login`, body);
  }

  /**
   * Registers a new clinic admin account and clinic.
   * @param data - Registration payload including clinic and user details
   * @returns Observable of the login API envelope with session tokens
   */
  register(data: {
    clinicName: string;
    email: string;
    username: string;
    passwordHash: string;
    firstName: string;
    lastName: string;
  }): Observable<ApiResponseEnvelope<LoginResponse>> {
    const body = {
      clinicName: data.clinicName,
      email: data.email,
      username: data.username,
      password: data.passwordHash,
      firstName: data.firstName,
      lastName: data.lastName,
    };
    return this.http.post<ApiResponseEnvelope<LoginResponse>>(`${this.baseUrl}/register`, body);
  }

  /**
   * Exchanges a refresh token for a new access token.
   * @param refreshToken - Valid refresh token from the prior login
   * @returns Observable of the refresh API envelope
   */
  refreshToken(refreshToken: string): Observable<ApiResponseEnvelope<RefreshTokenResponse>> {
    return this.http.post<ApiResponseEnvelope<RefreshTokenResponse>>(`${this.baseUrl}/refresh`, { refreshToken });
  }

  /**
   * Invalidates the current session on the server.
   * @returns Observable confirming logout success
   */
  logout(): Observable<ApiResponseEnvelope<{ success: boolean }>> {
    return this.http.post<ApiResponseEnvelope<{ success: boolean }>>(`${this.baseUrl}/logout`, {});
  }

  /**
   * Loads the currently authenticated user profile.
   * @returns Observable of the current user envelope
   */
  getMe(): Observable<ApiResponseEnvelope<User>> {
    return this.http.get<ApiResponseEnvelope<User>>(`${this.baseUrl}/me`);
  }

  /**
   * Updates profile fields for the authenticated user.
   * @param data - Partial profile fields to persist
   * @returns Observable of the updated user envelope
   */
  updateProfile(data: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    username?: string;
  }): Observable<ApiResponseEnvelope<User>> {
    return this.http.put<ApiResponseEnvelope<User>>(`${this.baseUrl}/profile`, data);
  }

  /**
   * Changes the authenticated user's password.
   * @param currentPassword - Existing password for verification
   * @param newPassword - Replacement password
   * @returns Observable confirming the password change
   */
  changePassword(currentPassword: string, newPassword: string): Observable<ApiResponseEnvelope<{ success: boolean }>> {
    return this.http.put<ApiResponseEnvelope<{ success: boolean }>>(`${this.baseUrl}/change-password`, {
      currentPassword,
      newPassword,
    });
  }
}
